import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchPendingCampaignQuestions,
  saveCampaignQuestionAnswer,
  type PendingCampaignQuestion,
} from '@/lib/landing-campaign-questionnaire'
import styles from './LandingProfileQuestionnaire.module.css'

type Props = { campaignId: number; achievementsReady: boolean }

export function LandingProfileQuestionnaire({ campaignId, achievementsReady }: Props) {
  const [questions, setQuestions] = useState<PendingCampaignQuestion[]>([])
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestId = useRef(0)
  const firstOptionRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLElement>(null)

  const load = async () => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      const result = await fetchPendingCampaignQuestions(campaignId)
      if (id === requestId.current) setQuestions(result.questions)
    } catch {
      if (id === requestId.current) setError('Não foi possível carregar as perguntas. Tente novamente.')
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    return () => { requestId.current += 1 }
    // Reload only when the campaign changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId])

  const question = questions[0]
  const questionId = question?.id
  const open = achievementsReady && !loading && Boolean(question || error)
  useEffect(() => {
    if (open && questionId) firstOptionRef.current?.focus()
  }, [open, questionId])

  useEffect(() => {
    if (!open) return
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalRef.current) return
      const items = Array.from(modalRef.current.querySelectorAll<HTMLElement>('input:not(:disabled), button:not(:disabled)'))
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previousFocus?.focus()
    }
  }, [open])

  async function submit() {
    if (!question || selectedOption === null || saving) return
    setSaving(true)
    setError(null)
    try {
      const result = await saveCampaignQuestionAnswer(campaignId, question.id, selectedOption)
      setQuestions(result.questions)
      setSelectedOption(null)
    } catch (cause) {
      if (cause instanceof ApiClientError && cause.status === 422) {
        await load()
      }
      setError('Não foi possível salvar sua resposta. Escolha uma opção e tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div className={styles.overlay}>
      <section ref={modalRef} className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="campaign-question-title">
        {question ? (
          <>
            <p className={styles.eyebrow}>Pergunta da campanha</p>
            <h2 id="campaign-question-title">{question.title}</h2>
            <p>Escolha uma opção para continuar sua jornada nesta campanha.</p>
            <div className={styles.options} role="radiogroup" aria-label={question.title}>
              {question.options.map((option, index) => (
                <label key={option.id} className={styles.option}>
                  <input
                    ref={index === 0 ? firstOptionRef : undefined}
                    type="radio"
                    name={`campaign-question-${question.id}`}
                    value={option.id}
                    checked={selectedOption === option.id}
                    onChange={() => setSelectedOption(option.id)}
                    disabled={saving}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {error && <p role="alert" className={styles.error}>{error}</p>}
            <button type="button" className={styles.button} onClick={() => void submit()} disabled={selectedOption === null || saving}>
              {saving ? 'Salvando...' : 'Salvar resposta'}
            </button>
          </>
        ) : (
          <>
            <h2 id="campaign-question-title">Perguntas da campanha</h2>
            <p role="alert" className={styles.error}>{error}</p>
            <button type="button" className={styles.button} onClick={() => void load()}>Tentar novamente</button>
          </>
        )}
      </section>
    </div>,
    document.body
  )
}
